"use client";

import { CircleCheck, Info } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useAccount } from "wagmi";
import { Button } from "@/components/ui/button";
import { useStatementVoteMine } from "@/hooks/statement/useStatementVoteMine";
import { formatDnm } from "@/lib/statement/formatDnm";
import { StatementVoteModal } from "./StatementVoteModal";

interface StatementVotePanelProps {
  statementId: string;
  index: number;
  isOpen: boolean;
}

const InfoNote: React.FC = () => {
  const t = useTranslations("statement");
  return (
    <div className="flex items-center gap-3">
      <Info className="size-5 shrink-0 text-text-secondary" aria-hidden />
      <p className="flex-1 text-sm leading-relaxed text-text-secondary">
        {t("vote.snapshotNote")}
      </p>
    </div>
  );
};

export const StatementVotePanel: React.FC<StatementVotePanelProps> = ({
  statementId,
  index,
  isOpen,
}) => {
  const t = useTranslations("statement");
  const { isConnected } = useAccount();
  const [open, setOpen] = useState(false);

  const mine = useStatementVoteMine(statementId, isConnected);
  const support =
    (mine.data as { data?: { support?: "yes" | "no" | null } } | undefined)
      ?.data?.support ?? null;
  const weight =
    (mine.data as { data?: { weight?: string } } | undefined)?.data?.weight ??
    "0";
  const hasVoted = support !== null;

  if (!isOpen) return null;

  if (!isConnected) {
    return (
      <p className="text-sm text-text-secondary">{t("vote.connectRequired")}</p>
    );
  }

  if (hasVoted) {
    // Green success badge — the "no green/red" rule is for the YES/NO vote
    // BUTTONS, not this confirmation. One vote, final — no cancel/withdraw;
    // budget auto-frees when the voting window closes.
    return (
      <div className="space-y-3">
        <InfoNote />
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-xs border border-semantic-success bg-semantic-success/15 px-4 py-2 text-sm font-semibold text-semantic-success">
            <CircleCheck className="size-5 shrink-0" aria-hidden />
            {t("vote.success", { dnm: formatDnm(weight) })}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Info className="size-5 shrink-0 text-text-secondary" aria-hidden />
        <p className="flex-1 text-sm leading-relaxed text-text-secondary">
          {t("vote.snapshotNote")}
        </p>
      </div>
      <div className="flex justify-center">
        <Button
          variant="solid"
          colors="primary"
          endIcon={<CircleCheck size={18} aria-hidden />}
          onClick={() => setOpen(true)}
        >
          {t("detail.voteCta")}
        </Button>
      </div>
      <StatementVoteModal
        open={open}
        onOpenChange={setOpen}
        statementId={statementId}
        index={index}
        currentSupport={support}
      />
    </div>
  );
};
